/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { MemoExoticComponent } from 'react';
import EmployeeCompensation from '../partials/charts/EmployeeCompensation';
import { PoliciesAndBestPracticesTable } from '../partials/charts/PoliciesAndBestPracticesTable';
import { chartStyleNames } from './chartStyles';
import { GenericChartProps, GenericDataProps, InsightPage, totalEmissionsUtrCodes } from './constants';
import { DataProcessor, insightChartSelectorsNames } from './selectors';
import { MainDownloadCode } from '../../../../config/app-config';

export enum LayoutColumnType {
  Heading = 'Heading',
  GenericChart = 'GenericChart',
  GenericData = 'GenericData',
  CustomComponent = 'CustomComponent',
}

interface LayoutColumnProps {
  title?: string;
  refCode?: string;
  columnSize?: number;
  type: LayoutColumnType;
  buttons?: (JSX.Element)[];
}

interface LayoutColumnChart extends LayoutColumnProps {
  type: LayoutColumnType.GenericChart;
  chartProps: Omit<GenericChartProps, 'width' | 'height'>;
}

interface LayoutColumnData extends LayoutColumnProps {
  type: LayoutColumnType.GenericData;
  dataProps: GenericDataProps;
}

interface LayoutColumnCustomComponent extends LayoutColumnProps {
  type: LayoutColumnType.CustomComponent;
  component: string
}

interface LayoutColumnHeading extends LayoutColumnProps {
  type: LayoutColumnType.Heading;
}

export type LayoutColumn = LayoutColumnChart | LayoutColumnData | LayoutColumnCustomComponent | LayoutColumnHeading

export interface LayoutRow {
  title?: string;
  columnCount?: number;
  columns: LayoutColumn[];
}

export const getChartComponent = (colComponent: string) => {
  return chartComponentMap[colComponent]
}

const chartComponentNames: { [key: string]: string } = {
  EmployeeCompensation: 'EmployeeCompensation',
  PoliciesAndBestPracticesTable: 'PoliciesAndBestPracticesTable'
}

export const chartComponentMap: { [key: string]: MemoExoticComponent<() => JSX.Element> } = {
  PoliciesAndBestPracticesTable,
  EmployeeCompensation
}

export const fixedHighlights: LayoutRow[] = [
  {
    columns: [
      {
        type: LayoutColumnType.GenericData,
        title: 'Greenhouse gas emissions',
        refCode: 'greenhouse gas emissions',
        dataProps: {
          variables: totalEmissionsUtrCodes,
          dataProcessor: DataProcessor.TableColumnSum
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Employee gender diversity',
        refCode: 'employee gender diversity',
        chartProps: {
          chartType: 'PieChart',
          settingsSelector: insightChartSelectorsNames.selectDataEmployeeGenderDiversity,
        }
      },
      {
        type: LayoutColumnType.GenericData,
        title: 'Anti-corruption policy communicated to',
        refCode: 'anticorruption policy to employees',
        dataProps: {
          variables: {
            a: {
              code: 'gri/2020/205-2/b',
              valueListCode: 'number',
            },
            b: {
              code: 'gri/2020/205-2/b',
              valueListCode: 'total_number_employees',
            }
          },
          dataProcessor: DataProcessor.TableColumnsPercentage,
          suffix: '%',
          unitText: 'of employees',
          decimalPlaces: 1,
        }
      }
    ]
  }
]

export const fixedHighlightsSgx: LayoutRow[] = [
  {
    columns: [
      { ...fixedHighlights[0].columns[0] },
      { ...fixedHighlights[0].columns[1] },
      { ...fixedHighlights[0].columns[2] },
    ]
  }
]

export const environment: LayoutRow[] = [
  {
    columns: [
      {
        type: LayoutColumnType.GenericData,
        title: 'Greenhouse gas emissions',
        refCode: 'greenhouse gas emissions',
        dataProps: {
          variables: totalEmissionsUtrCodes,
          dataProcessor: DataProcessor.TableColumnSum
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: '% emissions by scope',
        refCode: 'emissions by scope',
        chartProps: {
          chartType: 'PieChart',
          settingsSelector: insightChartSelectorsNames.selectDataGHGEmissionsDonut,
        }
      },
      {
        type: LayoutColumnType.GenericData,
        title: 'Green investment',
        refCode: 'green investment',
        dataProps: {
          variables: {
            a: {
              code: 'unctad/2020/a.3.1',
              valueListCode: 'pc_envt_exp',
            }
          },
          suffix: '%',
          unitText: 'of capex',
          dataProcessor: DataProcessor.TableColumnSum
        }
      }
    ]
  },
  {
    title: 'Emissions',
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Total emissions over time',
        refCode: 'total emissions over time',
        chartProps: {
          chartType: 'LineChart',
          settingsSelector: insightChartSelectorsNames.selectDataTotalEmissionsOverTime
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: '% emissions by scope',
        refCode: 'emissions by scope',
        chartProps: {
          chartType: 'LineChart',
          settingsSelector: insightChartSelectorsNames.selectDataGHGEmissionsPercentage
        }
      }
    ]
  },
  {
    title: 'Energy use',
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Paris agreement energy target: progress',
        refCode: 'paris agreement energy target: progress',
        chartProps: {
          chartType: 'LineChart',
          settingsSelector: insightChartSelectorsNames.selectDataParisAgreement
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: '% capex spent on sustainable energy',
        refCode: 'capex spent on sustainable energy',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataCapexSpentOnSustEnergy
        }
      }
    ]
  },
  {
    title: 'Waste',
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: '% plastic waste recycled',
        refCode: 'plastic waste recycled',
        chartProps: {
          chartType: 'LineChart',
          settingsSelector: insightChartSelectorsNames.selectDataPlasticWasteRecycled
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Hazardous and non-hazardous',
        refCode: 'hazardous and non-hazardous',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataHazardousAndNonHazardous
        }
      }
    ]
  }
];

export const social: LayoutRow[] = [
  {
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Employee numbers',
        refCode: 'employee numbers',
        chartProps: {
          chartType: 'BarChart',
          settingsSelector: insightChartSelectorsNames.selectDataEmployeeNumbers,
          options: chartStyleNames.chartStyleHorizontalBar
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Employee gender split',
        refCode: 'employee gender split',
        chartProps: {
          chartType: 'PieChart',
          settingsSelector: insightChartSelectorsNames.selectDataEmployeeGenderSplitDonut
        }
      },
      {
        type: LayoutColumnType.GenericData,
        title: 'Employee turnover ratio',
        refCode: 'employee turnover ratio',
        dataProps: {
          variables: {
            a: {
              code: 'survey/sdg/5.1/ratio-employee-turnover',
              valueListCode: 'ratio_turnover',
            }
          },
          decimalPlaces: 2,
          unitText: 'Ratio of employee turnover rate for women to men',
        }
      }
    ]
  },
  {
    title: 'Gender',
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Average female pay v male pay',
        refCode: 'average female pay v male pay',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataFemalePayMalePay
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Gender: average hours of training',
        refCode: 'gender: average hours of training',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataGenderAverageHoursTraining
        }
      }
    ]
  },
  {
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Employee gender split',
        refCode: 'employee gender split',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataEmployeeGenderSplit
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Turnover by gender',
        refCode: 'turnover by gender',
        chartProps: {
          chartType: 'LineChart',
          settingsSelector: insightChartSelectorsNames.selectDataTurnoverByGender
        }
      }
    ]
  },
  {
    title: 'Wages and health',
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: '% workforce paid the national living wage',
        refCode: 'workforce paid the national living wage',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataWorkforcePaidLivingWagePc
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Expenditure on employee H&S as a % of revenue',
        refCode: 'expenditure on employee health and safety',
        chartProps: {
          chartType: 'LineChart',
          settingsSelector: insightChartSelectorsNames.selectDataExpenditureOnEmployeeHealth
        }
      }
    ]
  }
]

export const governance: LayoutRow[] = [
  {
    columns: [
      {
        type: LayoutColumnType.CustomComponent,
        title: 'Employee compensation',
        refCode: 'employee compensation',
        component: chartComponentNames.EmployeeCompensation
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Board diversity',
        refCode: 'board diversity',
        chartProps: {
          chartType: 'PieChart',
          settingsSelector: insightChartSelectorsNames.selectDataBoardDiversity,
          options: chartStyleNames.chartStylePieLegend
        }
      },
      {
        type: LayoutColumnType.GenericData,
        title: '% women on the board',
        refCode: 'women on the board',
        dataProps: {
          variables: {
            a: {
              code: 'gri/2020/405-1/a',
              valueListCode: 'female',
            }
          },
          decimalPlaces: 0,
          suffix: '%',
          unitText: ''
        }
      }
    ]
  },
  {
    title: 'Diversity and compensation',
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Employee compensation v highest paid',
        refCode: 'employee compensation v highest paid',
        chartProps: {
          chartType: 'LineChart',
          settingsSelector: insightChartSelectorsNames.selectDataEmployeeCompensationVersusHighest
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Senior staff % bonuses based on ESG targets',
        refCode: 'senior staff % bonuses based on esg targets',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataSeniorStaffBonusBasedOnESG
        }
      }
    ]
  },
  {
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Board diversity: gender split',
        refCode: 'board diversity: gender split',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataBoardDiversityGenderSplit
        }
      }
    ]
  },
  {
    title: 'Corruption training and regulations',
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: '% of board with anti-corruption training',
        refCode: 'of board with anti-corruption training',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataBoardAntiCorruptionTrainingPc
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: '% operations in breach of financial regulation',
        refCode: 'operations in breach of financial regulation',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataOperationsInBreachPc
        }
      }
    ]
  },
  {
    title: 'Policies and best practice',
    columns: [
      {
        refCode: 'policies-and-best-practice',
        type: LayoutColumnType.CustomComponent,
        component: chartComponentNames.PoliciesAndBestPracticesTable,
        columnSize: 3,
      }
    ]
  },
];


export const environment_sgx: LayoutRow[] = [
  {
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Absolute GHG emissions by scope (%)',
        refCode: 'absolute ghg emissions by scope (%)',
        chartProps: {
          chartType: 'PieChart',
          settingsSelector: insightChartSelectorsNames.selectDataGHGEmissionsDonut,
        }
      },
      {
        type: LayoutColumnType.GenericData,
        title: 'Total energy consumption',
        refCode: 'total energy consumption',
        dataProps: {
          variables: {
            a: {
              code: 'gri/2020/302-1/e'
            }
          },
        }
      },
      {
        type: LayoutColumnType.GenericData,
        title: 'Total water consumption',
        refCode: 'total water consumption',
        dataProps: {
          variables: {
            a: {
              code: 'gri/2020/303-5/a'
            }
          },
        }
      }
    ]
  },
  {
    title: 'Greenhouse gas emissions',
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Total absolute emissions over time',
        refCode: 'total absolute emissions over time',
        chartProps: {
          chartType: 'LineChart',
          settingsSelector: insightChartSelectorsNames.selectDataTotalEmissionsOverTime
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Absolute emissions by scope over time',
        refCode: 'absolute emissions by scope over time',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataGHGEmissions,
          options: chartStyleNames.chartStyleStacked
        }
      }
    ]
  },
  {
    title: 'Energy and water consumption',
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Total energy consumption over time',
        refCode: 'total energy consumption over time',
        chartProps: {
          chartType: 'LineChart',
          settingsSelector: insightChartSelectorsNames.selectDataEnergyConsumption
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Total water consumption over time',
        refCode: 'total water consumption over time',
        chartProps: {
          chartType: 'LineChart',
          settingsSelector: insightChartSelectorsNames.selectDataWaterConsumption
        }
      }
    ]
  },
  {
    title: 'Waste',
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Hazardous and non-hazardous waste',
        refCode: 'hazardous and non-hazardous waste',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataHazardousAndNonHazardous,
          options: chartStyleNames.chartStyleStacked
        }
      }
    ]
  }
];

export const social_sgx: LayoutRow[] = [
  {
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Employee gender diversity',
        refCode: 'employee gender diversity',
        chartProps: {
          chartType: 'PieChart',
          settingsSelector: insightChartSelectorsNames.selectDataEmployeeGenderDiversity,
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Employee age diversity',
        refCode: 'employee age diversity',
        chartProps: {
          chartType: 'PieChart',
          settingsSelector: insightChartSelectorsNames.selectDataEmployeeAgeDiversity,
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Average training hours by gender',
        refCode: 'average training hours by gender',
        chartProps: {
          chartType: 'BarChart',
          settingsSelector: insightChartSelectorsNames.selectDataAverageHoursTrainingPerGender,
          options: chartStyleNames.chartStyleHorizontalBar
        }
      }
    ]
  },
  {
    columns: [
      {
        type: LayoutColumnType.GenericData,
        title: 'Total employee turnover',
        refCode: 'total employee turnover',
        dataProps: {
          variables: {
            a: {
              code: 'gri/2020/401-1/b',
              valueListCode: 'total_turnover'
            }
          },
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Employee turnover by gender',
        refCode: 'employee turnover by gender',
        chartProps: {
          chartType: 'PieChart',
          settingsSelector: insightChartSelectorsNames.selectDataEmployeeTurnoverByGender,
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Employee turnover by age',
        refCode: 'employee turnover by age',
        chartProps: {
          chartType: 'PieChart',
          settingsSelector: insightChartSelectorsNames.selectDataEmployeeTurnoverByAge,
        }
      }
    ]
  },
  {
    title: 'Diversity',
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Gender diversity over time',
        refCode: 'gender diversity over time',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataEmployeeGenderDiversityHistorical,
          options: chartStyleNames.chartStyleStacked
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Age diversity over time',
        refCode: 'age diversity over time',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataEmployeeAgeDiversityHistorical,
          options: chartStyleNames.chartStyleStacked
        }
      }
    ]
  },
  {
    title: 'Employee turnover',
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Employee turnover by gender',
        refCode: 'employee turnover by gender',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataEmployeeTurnoverByGenderHistorical,
          options: chartStyleNames.chartStyleStacked
        }
      },
      {
        type: LayoutColumnType.GenericChart,
        title: 'Employee turnover by age group',
        refCode: 'employee turnover by age group',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataEmployeeTurnoverByAgeHistorical,
          options: chartStyleNames.chartStyleStacked
        }
      }
    ]
  },
];

export const governance_sgx: LayoutRow[] = [
  {
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Board diversity',
        refCode: 'board diversity',
        chartProps: {
          chartType: 'PieChart',
          settingsSelector: insightChartSelectorsNames.selectDataBoardDiversity,
          options: chartStyleNames.chartStylePieLegend
        }
      },
      {
        type: LayoutColumnType.GenericData,
        title: '% of independent board directors',
        refCode: 'independent board directors',
        dataProps: {
          variables: {
            a: {
              code: 'sgx-custom-42',
              valueListCode: 'sgx42-percentage'
            }
          },
        }
      },
      {
        type: LayoutColumnType.GenericData,
        title: 'No. of confirmed corruption incidents',
        refCode: 'confirmed corruption incidents',
        dataProps: {
          variables: {
            a: {
              code: 'gri/2020/205-3/a',
              valueListCode: 'corruption_incidents_number'
            }
          },
        }
      },
    ]
  },
  {
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Board diversity: gender split',
        refCode: 'board diversity: gender split',
        chartProps: {
          chartType: 'ColumnChart',
          settingsSelector: insightChartSelectorsNames.selectDataBoardDiversityGenderSplit
        }
      }
    ]
  },
  {
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Total volunteering hours',
        refCode: 'total volunteering hours',
        chartProps: {
          chartType: 'LineChart',
          settingsSelector: insightChartSelectorsNames.selectDataVolunteeringHours
        }
      },
    ]
  },
  {
    columns: [
      {
        type: LayoutColumnType.GenericChart,
        title: 'Total charitable donations',
        refCode: 'total charitable donations',
        chartProps: {
          chartType: 'LineChart',
          settingsSelector: insightChartSelectorsNames.selectDataTotalCharitableDonations
        }
      },
    ]
  }
];

export const layoutOptions = [
  {
    code: MainDownloadCode.CTL,
    name: 'SDG Impact',
  },
  {
    code: MainDownloadCode.SGX_Metrics,
    name: 'SGX metrics',
  },
] as const

export const getLayout = (page: InsightPage, selectedPack: typeof layoutOptions[number]['code']) => {
  switch (page) {
    case InsightPage.Overview:
      return selectedPack === 'sgx_metrics' ? fixedHighlightsSgx : fixedHighlights;
    case InsightPage.Social:
      return selectedPack === 'sgx_metrics' ? social_sgx : social;
    case InsightPage.Governance:
      return selectedPack === 'sgx_metrics' ? governance_sgx : governance;
    case InsightPage.Environmental:
    default:
      return selectedPack === 'sgx_metrics' ? environment_sgx : environment;
  }
}
